import { useAudioPlayback } from '@livekit/components-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sentry } from '../../observability/sentry'
import { cn } from '../ui'

/**
 * Autoplay gate for viewers who arrived by direct link (MU 24. 8.): the
 * browser blocks sound until the page has a user gesture, so the presenter's
 * audio stays silent until they tap this. Rendered only while playback is
 * blocked; in-app navigation carries the click and it never appears.
 *
 * LiveKit's own `StartAudio` floats the `room.startAudio()` promise — a
 * refused resume left the button on screen with no message. This one
 * catches it and tells the viewer.
 */
export function EnableAudioButton({ className }: { className?: string }) {
	const { t } = useTranslation()
	const { canPlayAudio, startAudio } = useAudioPlayback()
	const [failed, setFailed] = useState(false)

	if (canPlayAudio) return null

	return (
		<div className={cn('flex flex-col items-center gap-1', className)}>
			<button
				type="button"
				className="btn btn-primary btn-sm"
				onClick={() => {
					setFailed(false)
					startAudio().catch((err: unknown) => {
						Sentry.captureException(err, { extra: { context: 'EnableAudioButton' } })
						setFailed(true)
					})
				}}
			>
				{t('viewer.enableAudio')}
			</button>
			{failed && (
				<p role="alert" className="text-error text-xs text-center">
					{t('viewer.enableAudioFailed')}
				</p>
			)}
		</div>
	)
}
